import React, { useEffect } from 'react';
import { Grid, App } from 'antd';
import { useMapController } from '../../hooks/useMapController';
import { MapContainer } from '../MapContainer';
import { ISiteVisit, ISiteVisitQuestion, isMapQuestion } from '../../types';
import { IMapController } from '../../../../lib/map';

const { useBreakpoint } = Grid;

interface SummaryMapViewProps {
  siteVisit: ISiteVisit;
  questions: ISiteVisitQuestion[];
  onMapControllerReady?: (mapController: IMapController) => void;
}

const SummaryMapView: React.FC<SummaryMapViewProps> = ({
  siteVisit,
  questions,
  onMapControllerReady,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const { message } = App.useApp();

  const handleError = (errorMessage: string) => {
    message.error(errorMessage);
  };

  const { mapContainer, loading, mapController } = useMapController({
    isMobile,
    onError: handleError,
    enableGeocoding: false,
    enableHistory: false,
    initialCenter: siteVisit?.map?.coordinates,
  });

  useEffect(() => {
    if (mapController && onMapControllerReady) {
      onMapControllerReady(mapController);
    }
  }, [mapController, onMapControllerReady]);

  useEffect(() => {
    if (!mapController) return;

    const renderGeometry = () => {
      if (mapController.isReady) {
        try {
          if (mapController.clearFeatures) {
            mapController.clearFeatures();
          }

          questions.forEach((question, questionIndex) => {
            if (!isMapQuestion(question)) return;

            if (question.result?.points && mapController.addMarker) {
              question.result.points.forEach((point, pointIndex) => {
                const markerId = `${question.id || questionIndex}-${point.id}`;
                const markerTitle = `${questionIndex + 1}.${pointIndex + 1}`;

                try {
                  mapController.addMarker!({
                    id: markerId,
                    coordinate: point.coordinate,
                    title: markerTitle,
                    color: question.colors?.point || '#1890ff',
                    size: 25,
                    draggable: false,
                    selected: false,
                  });
                } catch (error) {
                  console.error('Error adding marker:', error);
                }
              });

              if (
                question.mapType === 'line' &&
                question.result.points.length >= 2 &&
                mapController.addLine
              ) {
                const lineId = `line-${question.id || questionIndex}`;
                const coordinates = question.result.points.map(p => p.coordinate);

                try {
                  mapController.addLine({
                    id: lineId,
                    coordinates,
                    color: question.colors?.line || '#1890ff',
                    width: 3,
                  });
                } catch (error) {
                  console.error('Error adding line:', error);
                }
              }

              if (
                question.mapType === 'polygon' &&
                question.result.points.length >= 3 &&
                mapController.addPolygon
              ) {
                const polygonId = `polygon-${question.id || questionIndex}`;
                const coordinates = question.result.points.map(p => p.coordinate);

                try {
                  mapController.addPolygon({
                    id: polygonId,
                    coordinates,
                    fillColor: question.colors?.fill || 'rgba(24, 144, 255, 0.2)',
                    strokeColor: question.colors?.line || '#1890ff',
                    strokeWidth: 2,
                  });
                } catch (error) {
                  console.error('Error adding polygon:', error);
                }
              }
            }
          });
        } catch (error) {
          console.error('Error rendering geometry:', error);
          handleError('Failed to render map geometry');
        }
      } else {
        setTimeout(renderGeometry, 100); // TODO
      }
    };

    renderGeometry();
  }, [mapController, questions]);

  return (
    <MapContainer
      mapRef={mapContainer}
      loading={loading}
      isMobile={isMobile}
      showToolbar={false}
      mapController={mapController}
    />
  );
};

export default SummaryMapView;
